import { z } from 'zod';

import type { Task } from './model';
import type { Change, RecordLike } from './productivity';

const timestamp = z.number().int().nonnegative();

export const focusSchema = z.object({
  taskId: z.string().uuid(),
  deviceId: z.string().uuid(),
  offlineStart: z.boolean().default(false),
  startedAt: timestamp,
  endedAt: timestamp.nullable(),
  segments: z
    .array(
      z
        .object({
          id: z.string().uuid(),
          startedAt: timestamp,
          endedAt: timestamp.nullable(),
          lastSeenAt: timestamp,
          runtimeId: z.string().uuid(),
          confirmed: z.boolean(),
          interrupted: z.boolean().default(false),
          correctedMinutes: z.number().min(0).max(10080).nullable(),
          correctionNote: z.string().max(2000),
        })
        .refine(s => s.endedAt === null || s.endedAt >= s.startedAt, 'Segment end precedes start'),
    )
    .max(2000),
});

export type FocusSession = z.infer<typeof focusSchema>;
export type FocusSegment = FocusSession['segments'][number];

export function segmentMinutes(segment: FocusSegment, now = Date.now()) {
  return (
    segment.correctedMinutes ?? Math.max(0, (segment.endedAt ?? now) - segment.startedAt) / 60000
  );
}

export function focusTotals(records: RecordLike[], taskId?: string, now = Date.now()) {
  let confirmed = 0,
    proposed = 0;

  for (const record of records) {
    if (record.kind === 'focus' && (!taskId || (record.value as FocusSession).taskId === taskId)) {
      for (const segment of (record.value as FocusSession).segments) {
        if (segment.confirmed && segment.endedAt !== null) {
          confirmed += segmentMinutes(segment, now);
        } else {
          proposed += segmentMinutes(segment, now);
        }
      }
    }
  }

  return { confirmed, proposed };
}

export function focusOverlaps(records: RecordLike[], now = Date.now()) {
  const segments = records
    .filter(r => r.kind === 'focus')
    .flatMap(r =>
      (r.value as FocusSession).segments
        .filter(s => s.correctedMinutes !== 0)
        .map(s => ({
          recordId: r.id,
          taskId: (r.value as FocusSession).taskId,
          deviceId: (r.value as FocusSession).deviceId,
          segment: s,
        })),
    );

  const overlaps: [(typeof segments)[number], (typeof segments)[number]][] = [];

  for (let i = 0; i < segments.length; i++) {
    for (let j = i + 1; j < segments.length; j++) {
      const a = segments[i],
        b = segments[j];

      if (
        a.deviceId !== b.deviceId &&
        a.segment.startedAt < (b.segment.endedAt ?? now) &&
        b.segment.startedAt < (a.segment.endedAt ?? now)
      ) {
        overlaps.push([a, b]);
      }
    }
  }

  return overlaps;
}

export function focusReview(records: RecordLike[], now = Date.now()) {
  const sessions = records.filter(
    r =>
      r.kind === 'focus' &&
      (r.value as FocusSession).segments.some(s => !s.confirmed && s.endedAt !== null),
  );

  // An overlap whose every side the owner has confirmed has already been reviewed. It stays
  // labelled in Time history, but asking again would leave the banner permanently stuck.
  const overlaps = focusOverlaps(records, now).filter(pair =>
    pair.some(entry => !entry.segment.confirmed),
  );

  const taskIds = new Set([
    ...sessions.map(r => (r.value as FocusSession).taskId),
    ...overlaps.flatMap(pair => pair.map(segment => segment.taskId)),
  ]);

  return { sessions, overlaps, taskIds };
}

// Display persisted sessions independently of the tab/runtime that created them.
export function taskFocus(records: RecordLike[], taskId: string, deviceId: string) {
  const sessions = records
    .filter(r => r.kind === 'focus' && (r.value as FocusSession).taskId === taskId)
    .map(r => r.value as FocusSession)
    .sort((a, b) => b.startedAt - a.startedAt);

  const session =
    sessions.find(s => s.endedAt === null && s.deviceId === deviceId) ??
    sessions.find(s => s.endedAt === null) ??
    sessions[0];

  const running = session?.segments.find(s => s.endedAt === null);

  const elapsedMs =
    session?.segments
      .filter(s => s.endedAt !== null)
      .reduce((sum, s) => sum + segmentMinutes(s) * 60000, 0) ?? 0;

  const legacy = records.find(r => r.id === taskId && r.kind === 'task')?.value as Task | undefined;

  return {
    deviceId: session?.endedAt === null ? session.deviceId : deviceId,
    ...(session
      ? {
          runningSince: running?.startedAt ?? null,
          elapsedMs,
          sessionOpen: session.endedAt === null,
          lastBreakMs: elapsedMs,
        }
      : (legacy?.focus ?? {
          runningSince: null,
          elapsedMs: 0,
          sessionOpen: false,
          lastBreakMs: 0,
        })),
  };
}

export function focusChanges(
  records: RecordLike[],
  taskId: string,
  deviceId: string,
  runtimeId: string,
  action: 'start' | 'break' | 'end' | 'complete',
  context = '',
  now = Date.now(),
  offlineStart = false,
): Change[] {
  const record = records.find(r => r.id === taskId && r.kind === 'task');

  if (!record) {
    throw new Error('Task unavailable');
  }

  const task = record.value as Task;

  if (action === 'end' && !context.trim()) {
    throw new Error('Leave a stopping point before ending unfinished work.');
  }

  const open = records.find(
    r =>
      r.kind === 'focus' &&
      (r.value as FocusSession).taskId === taskId &&
      (r.value as FocusSession).deviceId === deviceId &&
      (r.value as FocusSession).endedAt === null,
  );

  const session: FocusSession = open
    ? structuredClone(open.value as FocusSession)
    : { taskId, deviceId, offlineStart, startedAt: now, endedAt: null, segments: [] };

  // Preserve the old prototype accumulator as a proposed segment, never as confirmed work.
  if (
    !open &&
    !records.some(r => r.kind === 'focus' && (r.value as FocusSession).taskId === taskId) &&
    task.focus &&
    (task.focus.elapsedMs || task.focus.runningSince)
  ) {
    const duration =
      task.focus.elapsedMs +
      (task.focus.runningSince ? Math.max(0, now - task.focus.runningSince) : 0);

    session.startedAt = Math.max(0, now - duration);

    session.segments.push({
      id: crypto.randomUUID(),
      startedAt: session.startedAt,
      endedAt: now,
      lastSeenAt: now,
      runtimeId,
      confirmed: false,
      interrupted: false,
      correctedMinutes: null,
      correctionNote: 'Imported from the prototype timer; confirm or correct this estimate.',
    });
  }

  const running = session.segments.find(s => s.endedAt === null);

  if (action === 'start') {
    if (task.done || task.dropped) {
      throw new Error('Reopen or restore this task before starting.');
    }

    if (
      records.some(
        r =>
          r.kind === 'focus' &&
          (r.value as FocusSession).deviceId === deviceId &&
          (r.value as FocusSession).taskId !== taskId &&
          (r.value as FocusSession).endedAt === null,
      )
    ) {
      throw new Error('Leave a stopping point before switching tasks.');
    }

    if (!running) {
      session.segments.push({
        id: crypto.randomUUID(),
        startedAt: now,
        endedAt: null,
        lastSeenAt: now,
        runtimeId,
        confirmed: false,
        interrupted: false,
        correctedMinutes: null,
        correctionNote: '',
      });
    }
  } else {
    if (running) {
      running.endedAt = now;

      // Liveness comes from the runtime that opened the interval still being the one closing it: a
      // reload, a crash or another tab changes runtimeId. Staleness of lastSeenAt is deliberately
      // not consulted, because the heartbeat cannot distinguish a suspended machine from the owner
      // simply working outside this tab, which is what a focus timer is for.
      running.confirmed =
        !running.interrupted &&
        running.runtimeId === runtimeId &&
        now - running.startedAt < 7200000;
    }

    if (action !== 'break') {
      session.endedAt = now;
    }
  }

  const runningAfter = session.segments.find(s => s.endedAt === null);

  const elapsedMs = session.segments
    .filter(s => s.endedAt !== null)
    .reduce((sum, s) => sum + segmentMinutes(s, now) * 60000, 0);

  const otherCompletions: Change[] =
    action === 'complete'
      ? records
          .filter(
            r =>
              r.kind === 'focus' &&
              r.id !== open?.id &&
              (r.value as FocusSession).taskId === taskId &&
              (r.value as FocusSession).endedAt === null,
          )
          .map(r => ({
            ...r,
            value: {
              ...(r.value as FocusSession),
              endedAt: now,
              segments: (r.value as FocusSession).segments.map(s =>
                s.endedAt === null
                  ? {
                      ...s,
                      endedAt: now,
                      confirmed: false,
                      correctionNote:
                        'Task completed from another session. Confirm or correct this interval.',
                    }
                  : s,
              ),
            },
          }))
      : [];

  return [
    ...otherCompletions,
    { id: open?.id ?? crypto.randomUUID(), kind: 'focus', value: session },
    {
      ...record,
      value: {
        ...task,
        done: action === 'complete' ? true : task.done,
        stoppingPoint: action === 'end' ? context.trim() : task.stoppingPoint,
        stoppingDraft: action === 'end' || action === 'complete' ? '' : task.stoppingDraft,
        focus: {
          runningSince: runningAfter?.startedAt ?? null,
          elapsedMs,
          lastBreakMs: action === 'break' ? elapsedMs : (task.focus?.lastBreakMs ?? 0),
          sessionOpen: session.endedAt === null,
        },
      },
    },
  ];
}
